// frontend/src/pages/VerifyCertificate.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../services/axios';
import { 
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon
} from '@heroicons/react/24/outline';

const VerifyCertificate = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        verifyCertificate();
    }, [token]);

    const verifyCertificate = async () => {
        try {
            const response = await axios.get(`/completion/verify/${token}`);
            setData(response.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid or expired certificate');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center px-4">
                <div className="text-center">
                    <ClockIcon className="w-16 h-16 mx-auto text-gray-400 animate-pulse" />
                    <p className="mt-4 text-gray-600 text-sm">Verifying certificate authenticity...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <XCircleIcon className="w-16 h-16 mx-auto text-red-500" />
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">Verification Failed</h2>
                    <p className="mt-2 text-sm text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    const { certificate, intern, transcript } = data || {};

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4">
            <div className="w-full space-y-6">
                {/* Header */}
                <div className="text-center">
                    <CheckCircleIcon className="w-16 h-16 mx-auto text-green-600" />
                    <h1 className="mt-4 text-3xl font-bold text-gray-900">Certificate Verified</h1>
                    <p className="text-sm text-gray-600">This digital credential is valid and officially issued</p>
                </div>

                {/* Certificate Card */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="bg-black text-white p-6">
                        <h2 className="text-xl font-semibold">{certificate?.certificate_title || 'Certificate of Completion'}</h2>
                        <p className="text-gray-300 text-sm">{certificate?.certificate_subtitle || 'AI Business Transformation Internship'}</p>
                    </div>

                    <div className="p-6 space-y-6">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900">
                                    {intern?.name || 'Intern'}
                                </h3>
                                {intern?.university && (
                                    <p className="text-sm text-gray-600">{intern.university}</p>
                                )}
                                {intern?.degree && (
                                    <p className="text-xs text-gray-500">{intern.degree}</p>
                                )}
                            </div>
                            {certificate?.qr_code_url && (
                                <div className="flex-shrink-0">
                                    <img 
                                        src={certificate.qr_code_url} 
                                        alt="QR Code" 
                                        className="w-24 h-24 border border-gray-200 rounded"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-b border-gray-100 py-4 text-xs">
                            <div>
                                <p className="text-gray-400">Credential ID</p>
                                <p className="font-semibold text-gray-900 mt-0.5">{certificate?.credential_id}</p>
                            </div>
                            <div>
                                <p className="text-gray-400">Issue Date</p>
                                <p className="font-semibold text-gray-900 mt-0.5">
                                    {new Date(certificate?.issue_date).toLocaleDateString()}
                                </p>
                            </div>
                            <div>
                                <p className="text-gray-400">Verified Hours</p>
                                <p className="font-semibold text-gray-900 mt-0.5">{certificate?.total_hours} Hours</p>
                            </div>
                            <div>
                                <p className="text-gray-400">Status</p>
                                <p className="font-semibold text-green-600 mt-0.5">Verified</p>
                            </div>
                        </div>

                        {certificate?.description && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-sm text-gray-700">{certificate.description}</p>
                            </div>
                        )}

                        {/* Verified Skills */}
                        {certificate?.skills_verified && certificate.skills_verified.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-gray-700 mb-2">Verified Competencies</p>
                                <div className="flex flex-wrap gap-2">
                                    {certificate.skills_verified.map((skill, index) => (
                                        <span key={index} className="text-xs px-2.5 py-1 bg-gray-100 text-gray-800 rounded font-medium">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="pt-2 text-xs text-gray-400 border-t border-gray-100 flex justify-between">
                            <span>Verified on {new Date().toLocaleDateString()}</span>
                            <span>Official AI Internship Registry</span>
                        </div>
                    </div>
                </div>

                {/* Transcript Summary */}
                {transcript && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
                        <h3 className="text-lg font-semibold text-gray-900">Transcript Summary</h3>
                        <div className="grid grid-cols-3 gap-4 text-center border-t border-gray-100 pt-3 text-xs">
                            <div>
                                <span className="text-gray-400 block">Total Approved Hours</span>
                                <span className="font-bold text-gray-900 text-sm">{transcript.total_hours_approved}</span>
                            </div>
                            <div>
                                <span className="text-gray-400 block">Business Assessments</span>
                                <span className="font-bold text-gray-900 text-sm">{transcript.business_assessment_count}</span>
                            </div>
                            <div>
                                <span className="text-gray-400 block">Process Maps</span>
                                <span className="font-bold text-gray-900 text-sm">{transcript.process_maps_count}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyCertificate;
